import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  LinearProgress,
  Paper,
  Alert,
  Tooltip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  Close,
  Check,
  HelpOutline,
  Undo,
  ExpandMore,
  ExpandLess,
  SearchOff,
  OpenInNew,
  PictureAsPdf,
} from "@mui/icons-material";
import Header from "../../components/Header";
import {
  useGetReviewQuery,
  useGetQueueQuery,
  useGetProgressQuery,
  useSubmitDecisionMutation,
  useUndoDecisionMutation,
  useGetUnpaywallLinkQuery,
  useSetFullTextUrlMutation,
  useAddRecordNoteMutation,
} from "../../services/review.service";

const SWIPE_THRESHOLD = 110;
const PRELOAD_WHEN_REMAINING_BELOW = 5;

function ProgressBar({ done, total }) {
  const pct = total > 0 ? (done / total) * 100 : 0;
  return (
    <Box sx={{ mb: 2 }}>
      <Box display="flex" justifyContent="space-between" mb={0.5}>
        <Typography variant="body2" color="text.secondary">
          {done} / {total} screened
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {Math.max(total - done, 0)} remaining
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4 }} />
    </Box>
  );
}

function StageCompleteScreen({ stage, reviewId }) {
  const navigate = useNavigate();
  return (
    <Paper sx={{ p: 4, textAlign: "center", mt: 4 }}>
      <Typography variant="h5" mb={1}>Stage {stage} complete 🎉</Typography>
      <Typography color="text.secondary" mb={3}>
        You've screened everything currently in your Stage {stage} queue.
      </Typography>
      <Box display="flex" gap={2} justifyContent="center">
        <Button variant="outlined" onClick={() => navigate(`/reviews/${reviewId}`)}>
          Back to review
        </Button>
        {stage < 3 && (
          <Button variant="contained" onClick={() => navigate(`/reviews/${reviewId}/screen/${stage + 1}`)}>
            Continue to Stage {stage + 1}
          </Button>
        )}
      </Box>
    </Paper>
  );
}

// Full-text helper links (section 5). DOI/PubMed/PMC/Scholar are built
// client-side from the record's own fields; the Unpaywall OA link is looked
// up (and cached) server-side since it needs a contact email + rate limiting.
function FullTextLinks({ record, reviewId }) {
  const { data: unpaywallData } = useGetUnpaywallLinkQuery(
    { reviewId, recordId: record._id },
    { skip: !record.doi }
  );

  const links = [];
  if (record.doi) links.push({ label: "DOI", href: `https://doi.org/${record.doi}` });
  if (record.pmid) links.push({ label: "PubMed", href: `https://pubmed.ncbi.nlm.nih.gov/${record.pmid}/` });
  if (record.pmcid) links.push({ label: "PMC", href: `https://www.ncbi.nlm.nih.gov/pmc/articles/${record.pmcid}/` });
  links.push({
    label: "Google Scholar",
    href: `https://scholar.google.com/scholar?q=${encodeURIComponent(record.title)}`,
  });
  if (unpaywallData?.oaUrl) {
    links.push({ label: "Open-access PDF", href: unpaywallData.oaUrl, icon: <PictureAsPdf fontSize="small" /> });
  }

  return (
    <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
      {links.map((link) => (
        <Button
          key={link.label}
          size="small"
          variant="outlined"
          component="a"
          href={link.href}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          startIcon={link.icon || <OpenInNew fontSize="small" />}
        >
          {link.label}
        </Button>
      ))}
    </Box>
  );
}

function FullTextUrlAndNotes({ record, reviewId, onUpdated }) {
  const [urlInput, setUrlInput] = useState(record.fullTextUrl || "");
  const [noteInput, setNoteInput] = useState("");
  const [setFullTextUrl] = useSetFullTextUrlMutation();
  const [addNote] = useAddRecordNoteMutation();

  useEffect(() => {
    setUrlInput(record.fullTextUrl || "");
  }, [record._id, record.fullTextUrl]);

  const saveUrl = async () => {
    const result = await setFullTextUrl({ reviewId, recordId: record._id, url: urlInput }).unwrap();
    onUpdated(result.record);
  };

  const submitNote = async () => {
    if (!noteInput.trim()) return;
    const result = await addNote({ reviewId, recordId: record._id, text: noteInput.trim() }).unwrap();
    onUpdated(result.record);
    setNoteInput("");
  };

  return (
    <Box mt={2} onClick={(e) => e.stopPropagation()}>
      <Box display="flex" gap={1} mb={1}>
        <TextField
          size="small"
          fullWidth
          label="Full-text URL (shared with the team)"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
        />
        <Button variant="outlined" onClick={saveUrl}>Save</Button>
      </Box>

      {record.notes?.length > 0 && (
        <List dense>
          {record.notes.map((note, i) => (
            <ListItem key={note._id || i} disableGutters>
              <ListItemText
                primary={note.text}
                secondary={note.author?.username ? `— ${note.author.username}` : undefined}
              />
            </ListItem>
          ))}
        </List>
      )}
      <Box display="flex" gap={1}>
        <TextField
          size="small"
          fullWidth
          label="Add a note (shared with the team)"
          value={noteInput}
          onChange={(e) => setNoteInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submitNote()}
        />
        <Button variant="outlined" onClick={submitNote}>Add</Button>
      </Box>
    </Box>
  );
}

const Screen = () => {
  const { reviewId, stage: stageParam } = useParams();
  const stage = Number(stageParam);
  const theme = useTheme();

  const [page, setPage] = useState(0);
  const [cards, setCards] = useState([]);
  const [index, setIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState("");
  const [pendingRetry, setPendingRetry] = useState(null);
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [excludeReason, setExcludeReason] = useState("");
  const [excludeNote, setExcludeNote] = useState("");

  const { data: reviewData } = useGetReviewQuery(reviewId);
  const { data: queueData, isFetching } = useGetQueueQuery({ reviewId, stage, page, pageSize: 20 });
  const { data: progressData } = useGetProgressQuery(reviewId);
  const [submitDecision] = useSubmitDecisionMutation();
  const [undoDecision] = useUndoDecisionMutation();

  // The route re-renders this same component instance when only :stage changes
  // (e.g. the "Continue to Stage 2" link), so local queue state has to be reset
  // explicitly here rather than relying on remount.
  useEffect(() => {
    setPage(0);
    setCards([]);
    setIndex(0);
    setExpanded(false);
    setError("");
    setPendingRetry(null);
  }, [reviewId, stage]);

  // Page 0 is the source of truth (e.g. right after an undo); later pages are preloaded appends.
  useEffect(() => {
    if (!queueData) return;
    if (page === 0) {
      setCards(queueData.records);
      setIndex(0);
    } else {
      setCards((prev) => {
        const known = new Set(prev.map((r) => r._id));
        return [...prev, ...queueData.records.filter((r) => !known.has(r._id))];
      });
    }
  }, [queueData, page]);

  const total = queueData?.total ?? 0;

  // Depends on `cards` (state), not `queueData` directly, so this only runs
  // once `cards` has actually settled to its new value -- avoiding a
  // same-commit race with the sync effect above reading a stale `cards`
  // closure and prematurely bumping `page`.
  useEffect(() => {
    const remainingLoaded = cards.length - index;
    const hasMoreOnServer = cards.length < total;
    if (!isFetching && hasMoreOnServer && remainingLoaded < PRELOAD_WHEN_REMAINING_BELOW) {
      setPage((p) => p + 1);
    }
  }, [cards, index, total, isFetching]);

  const currentCard = cards[index];
  const stageProgress = progressData?.[`stage${stage}`];
  const hasMoreOnServer = cards.length < total;
  const isComplete = Boolean(queueData) && index >= cards.length && !hasMoreOnServer && !isFetching;

  const decide = useCallback(
    async (value, extra = {}) => {
      if (!currentCard) return;
      setError("");
      setExpanded(false);
      setIndex((i) => i + 1); // optimistic advance
      try {
        await submitDecision({ reviewId, stage, recordId: currentCard._id, value, ...extra }).unwrap();
      } catch (err) {
        setIndex((i) => Math.max(i - 1, 0)); // revert on failure
        setError(err?.data?.message || `Could not save that decision.`);
        setPendingRetry({ value, extra });
      }
    },
    [currentCard, reviewId, stage, submitDecision]
  );

  const retry = useCallback(() => {
    if (pendingRetry) {
      setPendingRetry(null);
      decide(pendingRetry.value, pendingRetry.extra);
    }
  }, [pendingRetry, decide]);

  const undo = useCallback(async () => {
    setError("");
    try {
      await undoDecision({ reviewId, stage }).unwrap();
      setPage(0); // refetch page 0 fresh; the undone record is back in the queue somewhere in seeded order
    } catch (err) {
      setError(err?.data?.message || "Nothing to undo.");
    }
  }, [reviewId, stage, undoDecision]);

  // At Stage 3, excluding requires picking a reason first (section 4), so it
  // opens a dialog instead of submitting immediately.
  const triggerExclude = useCallback(() => {
    if (stage === 3) {
      setExcludeReason("");
      setExcludeNote("");
      setExcludeDialogOpen(true);
    } else {
      decide("exclude");
    }
  }, [stage, decide]);

  const confirmExclude = () => {
    if (!excludeReason) return;
    setExcludeDialogOpen(false);
    decide("exclude", { reason: excludeReason, note: excludeNote || undefined });
  };

  const updateCurrentCard = (updatedFields) => {
    setCards((prev) => prev.map((c, i) => (i === index ? { ...c, ...updatedFields } : c)));
  };

  // Keyboard shortcuts (section 6).
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (excludeDialogOpen) return;
      if (e.key === "ArrowRight") decide("include");
      else if (e.key === "ArrowLeft") triggerExclude();
      else if (e.key === "ArrowUp") decide("maybe");
      else if (e.key === "Backspace" || e.key === "u" || e.key === "U") undo();
      else if (e.key === " ") {
        e.preventDefault();
        setExpanded((x) => !x);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [decide, undo, triggerExclude, excludeDialogOpen]);

  // Pointer-drag swipe.
  const dragRef = useRef(null);
  const [dragStyle, setDragStyle] = useState({});
  const dragState = useRef(null);

  const onPointerDown = (e) => {
    // Don't start a drag (and don't capture the pointer) when the gesture
    // begins on a link, button, or form field nested in the card -- capturing
    // the pointer there redirects the resulting click away from that element
    // and onto the card itself, so e.g. the PubMed link "expands" the card
    // instead of opening in a new tab.
    if (e.target.closest("a, button, input, textarea")) return;
    dragState.current = { startX: e.clientX, startY: e.clientY, dx: 0, dy: 0 };
    dragRef.current?.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    dragState.current.dx = dx;
    dragState.current.dy = dy;
    setDragStyle({
      transform: `translate(${dx}px, ${dy}px) rotate(${dx / 20}deg)`,
      transition: "none",
    });
  };
  const onPointerUp = () => {
    const drag = dragState.current;
    dragState.current = null;
    if (!drag) return;
    const { dx, dy } = drag;
    setDragStyle({ transform: "", transition: "transform 0.2s ease" });
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > SWIPE_THRESHOLD) {
      if (dx > 0) decide("include");
      else triggerExclude();
    } else if (dy < -SWIPE_THRESHOLD) {
      decide("maybe");
    }
  };

  if (!currentCard && !isComplete) {
    return (
      <Box m="1.5rem 2.5rem">
        <Header title={`Stage ${stage} screening`} subtitle="Loading..." />
      </Box>
    );
  }

  const stageSubtitle = { 1: "Title only", 2: "Title and abstract", 3: "Full text" }[stage];
  const exclusionReasons = reviewData?.review?.exclusionReasons || [];

  return (
    <Box m="1.5rem 2.5rem" maxWidth="640px" mx="auto">
      <Header title={`Stage ${stage} screening`} subtitle={stageSubtitle} />

      {reviewData?.review?.criteria && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: theme.palette.background.alt }}>
          <Typography variant="caption" color="text.secondary">Screening criteria</Typography>
          <Typography variant="body2">{reviewData.review.criteria}</Typography>
        </Paper>
      )}

      {stageProgress && <ProgressBar done={stageProgress.done} total={stageProgress.total} />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={pendingRetry && <Button size="small" onClick={retry}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {isComplete ? (
        <StageCompleteScreen stage={stage} reviewId={reviewId} />
      ) : (
        <>
          <Paper
            ref={dragRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onClick={() => setExpanded((x) => !x)}
            sx={{
              p: 3,
              minHeight: 320,
              cursor: "grab",
              userSelect: "none",
              touchAction: "none",
              ...dragStyle,
            }}
            elevation={3}
          >
            <Typography variant="h6" gutterBottom>{currentCard.title}</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {[currentCard.journal, currentCard.year].filter(Boolean).join(" · ")}
            </Typography>

            {stage >= 2 && (
              <>
                {currentCard.authors?.length > 0 && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {currentCard.authors.join(", ")}
                  </Typography>
                )}
                {currentCard.abstract && (
                  <Box
                    sx={{
                      mt: 1,
                      display: "-webkit-box",
                      WebkitLineClamp: expanded ? "unset" : 5,
                      WebkitBoxOrient: "vertical",
                      overflow: expanded ? "visible" : "hidden",
                    }}
                  >
                    <Typography variant="body2">{currentCard.abstract}</Typography>
                  </Box>
                )}
                {currentCard.abstract && (
                  <Button
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setExpanded((x) => !x); }}
                    startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                  >
                    {expanded ? "Collapse" : "Expand abstract"}
                  </Button>
                )}
                {currentCard.pubTypes?.length > 0 && (
                  <Box mt={1}>
                    {currentCard.pubTypes.map((pt) => (
                      <Chip key={pt} label={pt} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                  </Box>
                )}
              </>
            )}

            {stage === 3 && (
              <>
                <Divider sx={{ my: 2 }} />
                <FullTextLinks record={currentCard} reviewId={reviewId} />
                <FullTextUrlAndNotes record={currentCard} reviewId={reviewId} onUpdated={updateCurrentCard} />
              </>
            )}
          </Paper>

          <Box display="flex" justifyContent="center" flexWrap="wrap" gap={2} mt={3}>
            <Tooltip title="Exclude (←)">
              <IconButton color="error" size="large" onClick={triggerExclude}>
                <Close fontSize="large" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Maybe (↑)">
              <IconButton color="warning" size="large" onClick={() => decide("maybe")}>
                <HelpOutline fontSize="large" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Include (→)">
              <IconButton color="success" size="large" onClick={() => decide("include")}>
                <Check fontSize="large" />
              </IconButton>
            </Tooltip>
            {stage === 3 && (
              <Tooltip title="Full text not retrieved">
                <IconButton onClick={() => decide("not_retrieved")}>
                  <SearchOff fontSize="large" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Undo (Backspace/U)">
              <IconButton onClick={undo}>
                <Undo />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}

      <Dialog open={excludeDialogOpen} onClose={() => setExcludeDialogOpen(false)} fullWidth>
        <DialogTitle>Exclude — reason required</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Exclusion reason"
            value={excludeReason}
            onChange={(e) => setExcludeReason(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          >
            {exclusionReasons.map((reason) => (
              <MenuItem key={reason} value={reason}>{reason}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Note (optional)"
            value={excludeNote}
            onChange={(e) => setExcludeNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExcludeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!excludeReason} onClick={confirmExclude}>
            Exclude
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Screen;
